import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import StatCard from '@/components/ui/StatCard';
import EmptyState from '@/components/ui/EmptyState';
import type { FiltrosLeads, Lead, LeadExport, OpcoesLeads, ResumoLeads } from '../types';
import {
  horaMinuto,
  montarCsvLeads,
  nomeArquivoLeads,
  normalizarLeads,
  normalizarOpcoes,
  normalizarResumo,
  opcoesVazias,
} from '../utils';
import FiltrosPublico from './FiltrosPublico';
import CartaoLead from './CartaoLead';
import FolhaLead from './FolhaLead';

const LIMITE = 50;

// ---------------------------------------------------------------------------
// Aba "Leads" do painel da plataforma (só admin). Junta quem passou pelos
// eventos: mostra números, filtra por público e deixa falar com cada pessoa.
// ---------------------------------------------------------------------------
export default function Leads() {
  const [resumo, setResumo] = useState<ResumoLeads | null>(null);
  const [opcoes, setOpcoes] = useState<OpcoesLeads>(() => opcoesVazias());
  const [grupo, setGrupo] = useState<FiltrosLeads>({ eventos: [], cidades: [], origens: [] });
  const [busca, setBusca] = useState('');
  const [desde, setDesde] = useState('');
  const [soWhatsapp, setSoWhatsapp] = useState(false);

  const [leads, setLeads] = useState<Lead[]>([]);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [sincronizando, setSincronizando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [aberto, setAberto] = useState<Lead | null>(null);

  const inicioRef = useRef(true);

  const filtrosEfetivos = useMemo<FiltrosLeads>(
    () => ({
      eventos: grupo.eventos,
      cidades: grupo.cidades,
      origens: grupo.origens,
      ...(soWhatsapp ? { aceita_whatsapp: true } : {}),
      ...(busca.trim() ? { busca: busca.trim() } : {}),
      ...(desde ? { desde } : {}),
    }),
    [grupo, busca, desde, soWhatsapp],
  );

  const recarregar = useCallback(async () => {
    setErro(null);
    try {
      const [rs, lista] = await Promise.all([
        supabase.rpc('leads_resumo', { p_filtros: filtrosEfetivos }),
        supabase.rpc('leads_listar', { p_filtros: filtrosEfetivos, p_limite: LIMITE, p_pagina: 0 }),
      ]);
      if (rs.error) throw rs.error;
      if (lista.error) throw lista.error;
      setResumo(normalizarResumo(rs.data));
      const l = normalizarLeads(lista.data);
      setLeads(l);
      setTemMais(l.length >= LIMITE);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    }
  }, [filtrosEfetivos]);

  // Carga inicial: opções de filtro + resumo + primeira página.
  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const [op, rs, lista] = await Promise.all([
          supabase.rpc('leads_opcoes'),
          supabase.rpc('leads_resumo', { p_filtros: {} }),
          supabase.rpc('leads_listar', { p_filtros: {}, p_limite: LIMITE, p_pagina: 0 }),
        ]);
        if (op.error) throw op.error;
        if (rs.error) throw rs.error;
        if (lista.error) throw lista.error;
        if (!ativo) return;
        setOpcoes(normalizarOpcoes(op.data));
        setResumo(normalizarResumo(rs.data));
        const l = normalizarLeads(lista.data);
        setLeads(l);
        setTemMais(l.length >= LIMITE);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Recarrega quando os filtros mudam (espera 400 ms).
  useEffect(() => {
    if (inicioRef.current) {
      inicioRef.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      void recarregar();
    }, 400);
    return () => window.clearTimeout(t);
  }, [recarregar]);

  const paginaAtual = Math.floor(leads.length / LIMITE);

  async function carregarMais() {
    setCarregandoMais(true);
    try {
      const { data, error } = await supabase.rpc('leads_listar', {
        p_filtros: filtrosEfetivos,
        p_limite: LIMITE,
        p_pagina: paginaAtual,
      });
      if (error) throw error;
      const l = normalizarLeads(data);
      setLeads((atual) => [...atual, ...l]);
      setTemMais(l.length >= LIMITE);
    } catch (e) {
      mostrarToast(mensagemAmigavel(e), 4000, 'erro');
    } finally {
      setCarregandoMais(false);
    }
  }

  async function sincronizar() {
    setSincronizando(true);
    try {
      const { error } = await supabase.rpc('leads_sincronizar_agora');
      if (error) throw error;
      mostrarToast('Leads atualizados.', 3000, 'sucesso');
      await recarregar();
    } catch (e) {
      mostrarToast(mensagemAmigavel(e), 4000, 'erro');
    } finally {
      setSincronizando(false);
    }
  }

  async function baixar() {
    setBaixando(true);
    try {
      const { data, error } = await supabase.rpc('leads_exportar', { p_filtros: filtrosEfetivos });
      if (error) throw error;
      const linhas = (Array.isArray(data) ? data : []) as LeadExport[];
      if (linhas.length === 0) {
        mostrarToast('Nenhum lead para baixar.', 3000);
        return;
      }
      const csv = montarCsvLeads(linhas);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeArquivoLeads();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      mostrarToast(`${linhas.length} leads baixados.`, 3000, 'sucesso');
    } catch (e) {
      mostrarToast(mensagemAmigavel(e), 4000, 'erro');
    } finally {
      setBaixando(false);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-[18px] bg-background-200/70" />
        <div className="h-20 animate-pulse rounded-[18px] bg-background-200/70" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Números */}
      <section>
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-5">
          <StatCard rotulo="Leads" valor={resumo?.total ?? 0} destaque icone="ri-contacts-line" />
          <StatCard
            rotulo="Com telefone"
            valor={resumo?.com_telefone ?? 0}
            icone="ri-phone-line"
          />
          <StatCard rotulo="Com conta no app" valor={resumo?.com_conta ?? 0} icone="ri-user-3-line" />
          <StatCard
            rotulo="Recebem avisos"
            valor={resumo?.recebem_avisos ?? 0}
            icone="ri-notification-3-line"
          />
          <StatCard
            rotulo="Aceitam WhatsApp"
            valor={resumo?.aceitam_whatsapp ?? 0}
            icone="ri-whatsapp-line"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-500">
            {resumo?.ultima_sincronizacao
              ? `Atualizado às ${horaMinuto(resumo.ultima_sincronizacao)}`
              : 'Sem registro de atualização ainda'}
          </p>
          <Botao
            variante="secundario"
            icone="ri-refresh-line"
            carregando={sincronizando}
            onClick={() => void sincronizar()}
          >
            Atualizar agora
          </Botao>
        </div>
      </section>

      {/* Filtros */}
      <section>
        <Cartao className="p-4 md:p-5">
          <div className="space-y-4">
            <FiltrosPublico opcoes={opcoes} grupo={grupo} onChange={setGrupo} />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSoWhatsapp((v) => !v)}
                aria-pressed={soWhatsapp}
                className={`aperta inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  soWhatsapp
                    ? 'border-success-300 bg-success-100 text-success-700'
                    : 'border-secondary-300 bg-white text-foreground-600 hover:bg-background-50'
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={soWhatsapp ? 'ri-whatsapp-fill' : 'ri-whatsapp-line'} aria-hidden />
                </span>
                Aceitaram WhatsApp
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Campo
                rotulo="Buscar"
                value={busca}
                onChange={setBusca}
                placeholder="Nome, telefone ou e-mail"
              />
              <Campo rotulo="Desde" type="date" value={desde} onChange={setDesde} />
            </div>
          </div>
        </Cartao>
      </section>

      {/* Baixar */}
      <section className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
          {temMais ? ' (mostrando os primeiros)' : ''}
        </p>
        <Botao
          variante="secundario"
          icone="ri-download-2-line"
          carregando={baixando}
          onClick={() => void baixar()}
        >
          Baixar planilha
        </Botao>
      </section>

      {/* Lista */}
      <section>
        {erro ? (
          <Cartao className="p-6 text-center">
            <p className="text-base font-semibold text-foreground-950">Não foi possível carregar</p>
            <p className="mt-1 text-sm text-foreground-600">{erro}</p>
            <div className="mt-5 flex justify-center">
              <Botao variante="primario" icone="ri-refresh-line" onClick={() => void recarregar()}>
                Tentar de novo
              </Botao>
            </div>
          </Cartao>
        ) : leads.length === 0 ? (
          <EmptyState
            icone="ri-contacts-line"
            titulo="Nenhum lead por aqui"
            descricao="Quando alguém passar por um evento, aparece nesta lista."
          />
        ) : (
          <div className="space-y-3">
            {leads.map((l) => (
              <CartaoLead key={l.id} lead={l} onClick={() => setAberto(l)} />
            ))}

            {temMais && (
              <div className="flex justify-center pt-2">
                <Botao
                  variante="secundario"
                  icone="ri-arrow-down-line"
                  carregando={carregandoMais}
                  onClick={() => void carregarMais()}
                >
                  Carregar mais
                </Botao>
              </div>
            )}
          </div>
        )}
      </section>

      <FolhaLead
        lead={aberto}
        aberto={Boolean(aberto)}
        aoFechar={() => setAberto(null)}
        aoMudar={() => void recarregar()}
      />
    </div>
  );
}